import { createUrl, restGet, restPost } from '~/src/dash/api/rest';
import {
  convertRuleTypeToString,
  IInventoryRulesResponse,
  IInventoryRules,
} from '~/src/dash/views/inventory-rules/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { isEmpty } from 'lodash';
import { OrderDirectionType } from '~/src/dash/components/tables/common';

export type TInventoryRulesOrderByKey =
  | 'ID'
  | 'ContentCategory'
  | 'ContentChannel'
  | 'ContentContext'
  | 'ContentDistributionID'
  | 'ContentDistributionName'
  | 'ContentEpisode'
  | 'ContentGenre'
  | 'ContentID'
  | 'ContentKeywords'
  | 'ContentLanguage'
  | 'ContentLength'
  | 'ContentNetwork'
  | 'ContentProdQuality'
  | 'ContentProducer'
  | 'ContentProducerID'
  | 'ContentRating'
  | 'ContentSeason'
  | 'ContentSeries'
  | 'ContentTaxonomy'
  | 'ContentTitle'
  | 'ContentURL'
  | 'Custom5'
  | 'Custom6'
  | 'Custom7'
  | 'Custom8'
  | 'Custom9'
  | 'Custom10'
  | 'Custom11'
  | 'Custom12'
  | 'Custom13'
  | 'Custom14'
  | 'Custom15'
  | 'Custom16'
  | 'Custom17'
  | 'Custom18'
  | 'EndTime'
  | 'Key'
  | 'StartTime'
  | 'Value';

export interface IInventoryRulesQueryParams {
  PageSize: number;
  Page: number;
  OrderDirection: OrderDirectionType;
  Timezone: string;
  StartDateFrom: string; // 'YYYY-MM-DD'
  StartDateTo: string; // 'YYYY-MM-DD'
  EndDateFrom: string; // 'YYYY-MM-DD'
  EndDateTo: string; // 'YYYY-MM-DD'
  OrderBy: TInventoryRulesOrderByKey;
  Search?: string; // Optional search parameter for server-side search
}

type IInventoryRulesFilterResponse = {
  InventoryRules: IInventoryRules[];
};

export const fetchInventoryRules = async (
  publisherID: string,
  query: IInventoryRulesQueryParams,
): Promise<IInventoryRulesResponse> => {
  const reqUrl = `${createUrl('/inventory_rules', publisherID, 'v1')}`;
  const response = await restPost<IInventoryRulesResponse>(reqUrl, query);
  return (response?.parsedBody as IInventoryRulesResponse) || {};
};

export async function fetchFormatTypes(
  publisherID: string,
): Promise<IInventoryRulesFilterResponse> {
  const reqUrl = createUrl('/inventory_rule_format_types', publisherID, 'v1');
  const response = await restGet<IInventoryRulesFilterResponse>(reqUrl, {});
  return response.parsedBody as IInventoryRulesFilterResponse;
}

export const generateFormatTypeOptionsList = (session: ISessionState): Promise<ISelectOption[]> => {
  return fetchFormatTypes(session.publisher.ID)
    .then((resp) => {
      const options: ISelectOption[] = [];
      resp.InventoryRules.forEach((inventoryRule) => {
        if (inventoryRule.RuleType > 0) {
          options.push({
            id: inventoryRule.RuleType,
            label: convertRuleTypeToString(inventoryRule.RuleType),
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchLookupKeys(publisherID: string): Promise<IInventoryRulesFilterResponse> {
  const reqUrl = createUrl('/inventory_rule_lookup_keys', publisherID, 'v1');
  const response = await restGet<IInventoryRulesFilterResponse>(reqUrl, {});
  return response.parsedBody as IInventoryRulesFilterResponse;
}

export const generateLookupKeyOptionsList = (session: ISessionState): Promise<ISelectOption[]> => {
  return fetchLookupKeys(session.publisher.ID)
    .then((resp) => {
      const options: ISelectOption[] = [];
      resp.InventoryRules.forEach((inventoryRule) => {
        if (!isEmpty(inventoryRule.LookupKey)) {
          options.push({
            id: inventoryRule.LookupKey,
            label: inventoryRule.LookupKey,
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchContentCategories(
  publisherID: string,
): Promise<IInventoryRulesFilterResponse> {
  const reqUrl = createUrl('/inventory_rule_content_categories', publisherID, 'v1');
  const response = await restGet<IInventoryRulesFilterResponse>(reqUrl, {});
  return response.parsedBody as IInventoryRulesFilterResponse;
}

export const generateContentCategoriesOptionsList = (
  session: ISessionState,
): Promise<ISelectOption[]> => {
  return fetchContentCategories(session.publisher.ID)
    .then((resp) => {
      const options: ISelectOption[] = [];
      resp.InventoryRules.forEach((inventoryRule) => {
        if (!isEmpty(inventoryRule.ContentCategory)) {
          options.push({
            id: inventoryRule.ContentCategory,
            label: inventoryRule.ContentCategory,
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchContentGenres(
  publisherID: string,
): Promise<IInventoryRulesFilterResponse> {
  const reqUrl = createUrl('/inventory_rule_content_genres', publisherID, 'v1');
  const response = await restGet<IInventoryRulesFilterResponse>(reqUrl, {});
  return response.parsedBody as IInventoryRulesFilterResponse;
}

export const generateContentGenreOptionsList = (
  session: ISessionState,
): Promise<ISelectOption[]> => {
  return fetchContentGenres(session.publisher.ID)
    .then((resp) => {
      const options: ISelectOption[] = [];
      resp.InventoryRules.forEach((inventoryRule) => {
        if (!isEmpty(inventoryRule.ContentGenre)) {
          options.push({
            id: inventoryRule.ContentGenre,
            label: inventoryRule.ContentGenre,
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchContentNetworks(
  publisherID: string,
): Promise<IInventoryRulesFilterResponse> {
  const reqUrl = createUrl('/inventory_rule_content_networks', publisherID, 'v1');
  const response = await restGet<IInventoryRulesFilterResponse>(reqUrl, {});
  return response.parsedBody as IInventoryRulesFilterResponse;
}

export const generateContentNetworkOptionsList = (
  session: ISessionState,
): Promise<ISelectOption[]> => {
  return fetchContentNetworks(session.publisher.ID)
    .then((resp) => {
      const options: ISelectOption[] = [];
      resp.InventoryRules.forEach((inventoryRule) => {
        if (!isEmpty(inventoryRule.ContentNetwork)) {
          options.push({
            id: inventoryRule.ContentNetwork,
            label: inventoryRule.ContentNetwork,
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchContentTitles(
  publisherID: string,
): Promise<IInventoryRulesFilterResponse> {
  const reqUrl = createUrl('/inventory_rule_content_titles', publisherID, 'v1');
  const response = await restGet<IInventoryRulesFilterResponse>(reqUrl, {});
  return response.parsedBody as IInventoryRulesFilterResponse;
}

export const generateContentTitleOptionsList = (
  session: ISessionState,
): Promise<ISelectOption[]> => {
  return fetchContentTitles(session.publisher.ID)
    .then((resp) => {
      const options: ISelectOption[] = [];
      resp.InventoryRules.forEach((inventoryRule) => {
        if (!isEmpty(inventoryRule.ContentTitle)) {
          options.push({
            id: inventoryRule.ContentTitle,
            label: inventoryRule.ContentTitle,
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};
